import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  ArrowRight,
  Ban,
  Banknote,
  Car,
  CheckCircle,
  Clock3,
  CreditCard,
  FileText,
  Gift,
  History,
  MapPin,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  UserRound,
  UsersRound,
  Wallet,
  X,
} from 'lucide-react';
import { Skeleton, Empty } from '../ui/kit';
import { useAuth } from '../App';
import './Users.css';
import UserPaymentMethods from './UserPaymentMethods';
import AccountProtection from './AccountProtection';
import { datumUndZeit, nurDatum } from '../lib/zeit';

const statusColors = {
  searching: '#6b7280',
  accepted: '#586b87',
  arrived: '#7c5a91',
  in_progress: '#9c27b0',
  completed: '#4b7c58',
  cancelled: '#b84b4b',
};

const statusLabels = {
  searching: 'Suche',
  accepted: 'Angenommen',
  arrived: 'Angekommen',
  in_progress: 'Unterwegs',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

const recentStatusFilters = [
  { value: '', label: 'Alle aktuellen' },
  { value: 'in_progress', label: 'Unterwegs' },
  { value: 'completed', label: 'Abgeschlossen' },
  { value: 'cancelled', label: 'Storniert' },
];

const DIRECTORY_LIMIT = 25;

const accountStatusLabels = {
  active: 'Aktiv',
  banned: 'Gesperrt',
  deleted: 'Gelöscht',
};

// KONTO-TRENNUNG-001 (Owner-Entscheid 04.09.2026): Kunde, Fahrer, Partner sind GETRENNTE Konten —
// auch bei gleicher E-Mail. Der Admin muss AM ORT DER AKTION sehen, welches Konto er bearbeitet.
// Diese Bezeichner stehen deshalb im Bestätigungsdialog, im Profil-Titel und im 2FA-Dialog.
export const accountKindLabels = { customer: 'Kundenkonto', driver: 'Fahrerkonto', partner: 'Partnerkonto', admin: 'Admin-Konto' };
export const accountKindLabel = (role) => accountKindLabels[role] || `Konto (${role || 'unbekannt'})`;
const roleLabels = { customer: 'Kunde', driver: 'Fahrer', partner: 'Partner', admin: 'Admin' };

const fmtDate = (value, withTime = false) => (withTime ? datumUndZeit(value) : nurDatum(value));

const initials = (name) => String(name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part.charAt(0))
  .join('')
  .toUpperCase();

function SecurityDialog({ user, onClose, returnFocus }) {
  const [security, setSecurity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [mutationError, setMutationError] = useState('');
  const [saving, setSaving] = useState(false);
  const loadRequest = useRef(0);
  const mutationRequest = useRef(0);
  const savingRef = useRef(false);
  const dialogRef = useRef(null);

  const loadSecurity = useCallback(() => {
    const requestId = loadRequest.current + 1;
    loadRequest.current = requestId;
    setLoading(true);
    setLoadError(false);
    setMutationError('');
    api.get(`/admin/users/${user.id}/security`)
      .then(response => {
        if (requestId !== loadRequest.current) return;
        setSecurity(response.data);
        setLoading(false);
      })
      .catch(() => {
        if (requestId !== loadRequest.current) return;
        setSecurity(null);
        setLoadError(true);
        setLoading(false);
      });
  }, [user.id]);

  useEffect(() => {
    loadSecurity();
    return () => {
      loadRequest.current += 1;
      mutationRequest.current += 1;
      savingRef.current = false;
    };
  }, [loadSecurity]);

  useEffect(() => () => {
    if (returnFocus?.isConnected) returnFocus.focus();
  }, [returnFocus]);

  const close = useCallback(() => {
    if (savingRef.current) return;
    loadRequest.current += 1;
    mutationRequest.current += 1;
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (saving) dialogRef.current?.focus();
  }, [saving]);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...(dialogRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) || [])];
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close]);

  const account = security?.user;
  const sms2fa = security?.sms_2fa;
  const savedEnabled = Boolean(account?.two_factor_enabled);
  const effectiveEnabled = Boolean(account?.two_factor_effective);
  const globalEnforced = Boolean(sms2fa?.global_enforced);
  const canManage = security?.can_manage === true;

  let blockedReason = '';
  if (!canManage) blockedReason = 'Dieses Konto darf hier nicht geändert werden.';
  else if (globalEnforced && savedEnabled) blockedReason = 'SMS-2FA wird global erzwungen und kann hier nicht ausgeschaltet werden.';
  else if (!savedEnabled && !account?.has_phone) blockedReason = 'Für dieses Konto ist keine Telefonnummer vorhanden.';
  else if (!savedEnabled && !account?.phone_verified) blockedReason = 'Die Telefonnummer ist nicht verifiziert.';

  const toggleTwoFactor = async () => {
    if (!account || blockedReason || savingRef.current) return;
    const enabled = !savedEnabled;
    if (!window.confirm(`SMS-2FA für „${account.name || user.name}“ wirklich ${enabled ? 'einschalten' : 'ausschalten'}?`)) return;

    const requestId = mutationRequest.current + 1;
    mutationRequest.current = requestId;
    savingRef.current = true;
    setSaving(true);
    setMutationError('');
    try {
      const response = await api.patch(`/admin/users/${account.id}/2fa`, { enabled });
      if (requestId !== mutationRequest.current) return;
      setSecurity(response.data);
      toast.success(`SMS-2FA für ${account.name || user.name} ${enabled ? 'eingeschaltet' : 'ausgeschaltet'}`);
    } catch (error) {
      if (requestId !== mutationRequest.current) return;
      const serverMessage = error?.response?.data?.message || error?.response?.data?.error;
      const message = typeof serverMessage === 'string' && serverMessage.length <= 180
        ? serverMessage
        : 'SMS-2FA konnte nicht geändert werden. Bitte erneut versuchen.';
      setMutationError(message);
      toast.error(message);
    } finally {
      if (requestId === mutationRequest.current) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  };

  return (
    <div
      className="users-modal-backdrop"
      onMouseDown={event => event.target === event.currentTarget && !savingRef.current && close()}
    >
      <section
        ref={dialogRef}
        className="users-security-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="users-security-title"
        aria-busy={saving}
        tabIndex={-1}
      >
        <header>
          <div>
            <span className="users-overline">Kontosicherheit</span>
            <h2 id="users-security-title">SMS-Zwei-Faktor-Anmeldung</h2>
          </div>
          <button type="button" onClick={close} aria-label="Sicherheitsdialog schließen" disabled={saving} autoFocus><X size={16} /></button>
        </header>

        {loading ? (
          <div className="users-security-loading" aria-live="polite">
            <Skeleton h={148} r={12} />
            <span>Sicherheitsstatus wird geladen …</span>
          </div>
        ) : loadError ? (
          <div className="users-security-error" role="alert">
            <span>Sicherheitsstatus konnte nicht geladen werden.</span>
            <button type="button" onClick={loadSecurity}><RefreshCw size={12} /> Erneut versuchen</button>
          </div>
        ) : account ? (
          <div className="users-security-content">
            <div className="users-security-account">
              <span>{initials(account.name)}</span>
              <div>
                <strong>{account.name || 'Unbenanntes Konto'}</strong>
                <small>{accountKindLabel(account.role)} · {accountStatusLabels[account.status] || account.status}</small>
              </div>
            </div>

            <div className="users-security-states" aria-label="Zwei-Faktor-Status">
              <div>
                <small>Individuell gespeichert</small>
                <strong className={savedEnabled ? 'is-on' : 'is-off'}>Gespeichert: {savedEnabled ? 'AN' : 'AUS'}</strong>
              </div>
              <div>
                <small>Beim nächsten Login wirksam</small>
                <strong className={effectiveEnabled ? 'is-on' : 'is-off'}>Effektiv: {effectiveEnabled ? 'AN' : 'AUS'}</strong>
              </div>
            </div>

            <div className="users-security-facts">
              <span className={sms2fa?.sms_configured ? 'is-ready' : 'is-blocked'}>
                SMS-Versand {sms2fa?.sms_configured ? 'bereit' : 'nicht konfiguriert'}
              </span>
              <span className={account.has_phone ? 'is-ready' : 'is-blocked'}>
                Telefonnummer {account.has_phone ? 'vorhanden' : 'fehlt'}
              </span>
              <span className={account.phone_verified ? 'is-ready' : 'is-blocked'}>
                Telefonnummer {account.phone_verified ? 'verifiziert' : 'nicht verifiziert'}
              </span>
            </div>

            {globalEnforced && (
              <p className="users-security-global" id="users-security-global">
                <ShieldCheck size={15} /> SMS-2FA wird global erzwungen: Der effektive Zustand bleibt AN,
                unabhängig vom individuell gespeicherten Wert.
              </p>
            )}
            {!sms2fa?.sms_configured && (
              <p className="users-security-provider" id="users-security-provider">
                Der individuelle Wert kann gespeichert werden. Effektiv wird SMS-2FA erst, sobald der SMS-Versand konfiguriert ist.
              </p>
            )}
            {blockedReason && <p className="users-security-blocked" id="users-security-blocked">{blockedReason}</p>}
            {mutationError && <p className="users-security-mutation-error" role="alert">{mutationError}</p>}

            <div className="users-security-control">
              <div>
                <strong>Individuelle SMS-2FA</strong>
                <small id="users-security-guidance">
                  AUS bedeutet Ein-Faktor-Anmeldung. AN verlangt nach dem Passwort zusätzlich einen Code an die hinterlegte Nummer.
                </small>
              </div>
              <button
                type="button"
                className={`users-security-switch ${savedEnabled ? 'is-on' : ''}`}
                role="switch"
                aria-checked={savedEnabled}
                aria-label={`Individuelle SMS-2FA für ${account.name || user.name}`}
                aria-describedby={[
                  'users-security-guidance',
                  !sms2fa?.sms_configured && 'users-security-provider',
                  blockedReason && 'users-security-blocked',
                ].filter(Boolean).join(' ')}
                disabled={Boolean(blockedReason) || saving}
                onClick={toggleTwoFactor}
              >
                <span aria-hidden="true" />
              </button>
            </div>
            {saving && <p className="users-security-saving" aria-live="polite">Wird gespeichert …</p>}
          </div>
        ) : null}
      </section>
    </div>
  );
}

// ── KUNDEN-ZAHLUNGSAKTE-001: Zahlungsverlauf + Zahlungsakte im Kundenprofil ──────────────────
const fmtEuroCents = (cents) => `${(Number(cents || 0) / 100).toFixed(2)} €`;
const fmtEuro = (value) => `${Number(value || 0).toFixed(2)} €`;
const parseTs = (value) => {
  if (!value) return 0;
  const text = String(value);
  const time = new Date(text.includes('T') ? text : `${text.replace(' ', 'T')}Z`).getTime();
  return Number.isNaN(time) ? 0 : time;
};
// REVIEW-FIX-001 (P2): SQLite-`datetime('now')` ist UTC ohne Zeitzonen-Suffix — `new Date()`
// läse das als Ortszeit und zeigte in Berlin 1–2 h falsch an. Die Normalisierung sitzt jetzt
// EINMAL in lib/zeit (ZEIT-HELFER-ADMIN-001); fmtDbDate bleibt als Name für Aufrufer und Tests.
export const fmtDbDate = fmtDate;

const PAYMENT_ERROR_LABELS = {
  card_declined: 'Karte abgelehnt',
  insufficient_funds: 'Karte nicht gedeckt',
  authentication_required: 'Karten-Bestätigung (3-D Secure) erforderlich',
  expired_card: 'Karte abgelaufen',
  payment_method_required: 'Keine Standardkarte hinterlegt',
  payment_collection_failed: 'Einzug fehlgeschlagen — Karte konnte nicht belastet werden',
};
const paymentErrorLabel = (code) => (code ? (PAYMENT_ERROR_LABELS[code] || code) : null);

const WALLET_KIND_LABELS = {
  ride: 'Fahrt (Guthaben eingesetzt)',
  cancellation_fee: 'Stornogebühr',
  cancellation_fee_settlement: 'Stornogebühr beglichen',
  cancellation_fee_waiver: 'Stornogebühr erlassen',
  // STRIPE-AUFSTOCKUNG-ADMIN-001: Fahrpreisrest aus Admin-Teileinzug (zählt wie eine Stornogebühr).
  fare_shortfall: 'Fahrpreisrest offen',
  referral: 'Werbeprämie',
  refund: 'Erstattung',
  promo: 'Aktionsguthaben',
  adjustment: 'Korrektur',
  topup: 'Aufladung',
};

const walletMethodLabel = (walletType) => (walletType === 'apple_pay'
  ? 'Apple Pay' : walletType === 'google_pay' ? 'Google Pay' : null);

// Die eine Ampel je Fahrt: aus gespeicherten Feldern ABGELEITET, nie erfunden.
// REVIEW-FIX-001 (P1): Die Ableitung folgt den BETRÄGEN, nicht der Zahlart. Eine Karten-Fahrt,
// die das Guthaben voll deckt, endet regulär mit `payment_status='canceled'` (Autorisierung
// wird freigegeben, nichts eingezogen) — das ist BEZAHLT, kein Fehlschlag. Nur wenn wirklich
// ein Kartenanteil (> 0) eingezogen werden sollte, urteilt der Kartenzustand.
export function paymentStanding(ride) {
  const cash = Number(ride.cash_amount || 0);
  const card = Number(ride.card_amount || 0);
  const wallet = Number(ride.wallet_amount || 0);
  const cardStanding = (subject) => {
    if (ride.payment_status === 'succeeded') return { label: `${subject} per Karte bezahlt (Stripe bestätigt)`, tone: 'ok' };
    // REVIEW-FIX-002 (P1): `collection_failed` schreibt NUR die Outbox an die FAHRT — der
    // Attempt bleibt dabei `authorized`/`capture_pending`. Ohne diese Zeile stünde ein
    // gescheiterter Einzug als „läuft" da: die falsche finanzielle Aussage im Reklamationsfall.
    if (ride.payment_status === 'collection_failed') return { label: `${subject}: Einzug fehlgeschlagen`, tone: 'bad' };
    if (ride.attempt_state === 'authorization_failed') return { label: `${subject}: Karte abgelehnt`, tone: 'bad' };
    if (ride.attempt_state === 'canceled') return { label: `${subject}: Einzug aufgehoben — nicht eingezogen`, tone: 'bad' };
    if (ride.attempt_state) return { label: `${subject}: Karteneinzug läuft`, tone: 'warn' };
    return { label: `${subject}: Karte — kein Einzugsvorgang`, tone: 'warn' };
  };
  if (ride.status === 'cancelled') {
    if (!(Number(ride.cancellation_fee || 0) > 0)) return { label: 'Storniert — kein Geld bewegt', tone: 'muted' };
    const feeName = ride.no_show ? 'No-Show-Gebühr' : 'Stornogebühr';
    if (card > 0) return cardStanding(feeName);
    // Voll übers Guthaben verrechnet — ob damit „beglichen" oder „offen (Konto im Minus)",
    // sagen die Einzelposten darunter; die Ampel behauptet hier bewusst nichts Falsches.
    return { label: `${feeName} über Guthaben verrechnet`, tone: 'warn' };
  }
  if (cash > 0) return { label: wallet > 0 ? 'Bar kassiert + Guthaben eingesetzt' : 'Bar kassiert (Fahrtende bestätigt)', tone: 'ok' };
  if (card > 0) return cardStanding('Fahrpreis');
  if (wallet > 0) return { label: 'Vollständig mit Guthaben bezahlt', tone: 'ok' };
  return { label: '0 € — nichts einzuziehen', tone: 'muted' };
}

const splitSummary = (ride) => [
  Number(ride.wallet_amount || 0) > 0 && `Guthaben ${fmtEuro(ride.wallet_amount)}`,
  Number(ride.cash_amount || 0) > 0 && `Bar ${fmtEuro(ride.cash_amount)}`,
  Number(ride.card_amount || 0) > 0 && `Karte ${fmtEuro(ride.card_amount)}`,
].filter(Boolean).join(' · ');

const WEBHOOK_EVENT_LABELS = {
  'payment_intent.amount_capturable_updated': 'Karte autorisiert (Stripe)',
  'payment_intent.succeeded': 'Karteneinzug bestätigt (Stripe)',
  'payment_intent.payment_failed': 'Kartenzahlung fehlgeschlagen (Stripe)',
  'payment_intent.canceled': 'Autorisierung aufgehoben (Stripe)',
};
const AUDIT_EVENT_LABELS = {
  ride_settlement: 'Abrechnung gebucht',
  cancellation_fee: 'Stornogebühr gebucht',
  cancellation_fee_settled: 'Stornogebühr beglichen',
  cancellation_fee_waived: 'Stornogebühr erlassen',
  no_show_fee: 'No-Show-Gebühr gebucht',
  tip: 'Trinkgeld gebucht',
  partner_payout: 'Partner-Auszahlung',
};

// Alle Wahrheiten der Akte als EINE zeitlich sortierte Kette — jede Zeile trägt ihre Quelle.
export function paymentFileTimeline(file) {
  const rows = [];
  const ride = file.ride || {};
  if (ride.created_at) rows.push({ ts: parseTs(ride.created_at), source: 'Fahrt', label: 'Fahrt erstellt' });
  for (const event of file.collect_events || []) {
    rows.push({
      ts: parseTs(event.created_at),
      source: 'Fahrer',
      label: `Fahrer verlangt ${fmtEuroCents(event.amount_cents)} (${event.method === 'cash' ? 'bar' : 'Karte'})`,
      detail: Number(event.open_fee_cents) > 0
        ? `davon offene Stornogebühr ${fmtEuroCents(event.open_fee_cents)}` : null,
    });
  }
  if (ride.completed_at && ride.status === 'completed') {
    rows.push({
      ts: parseTs(ride.completed_at),
      source: 'Fahrt',
      label: Number(ride.cash_amount || 0) > 0
        ? `Fahrt beendet — ${fmtEuro(ride.cash_amount)} bar kassiert`
        : 'Fahrt beendet',
    });
  }
  for (const event of file.webhook_events || []) {
    // REVIEW-FIX-001 (P2): jedes Ereignis behält seine Zuordnung — sonst vermischen sich
    // Fahrt-, Trinkgeld- und ersetzte Autorisierungs-Ereignisse zu einer anonymen Kette.
    const belongsTo = event.belongs_to === 'tip' ? 'Trinkgeld: '
      : event.belongs_to === 'ride_superseded' ? 'Ersetzte Autorisierung: ' : '';
    rows.push({
      ts: Number(event.provider_created_at || 0) * 1000,
      source: 'Stripe',
      label: belongsTo + (WEBHOOK_EVENT_LABELS[event.event_type] || event.event_type),
      detail: [
        Number(event.captured_cents) > 0 && `eingezogen ${fmtEuroCents(event.captured_cents)}`,
        event.provider_status && `Status ${event.provider_status}`,
        event.applied ? 'angewendet' : 'nicht angewendet',
      ].filter(Boolean).join(' · '),
    });
  }
  for (const entry of file.money_audit || []) {
    rows.push({
      ts: parseTs(entry.ts),
      source: 'Geldbuch',
      label: AUDIT_EVENT_LABELS[entry.event] || entry.event,
      detail: Number(entry.amount) ? fmtEuro(entry.amount) : null,
    });
  }
  for (const tx of file.wallet_transactions || []) {
    const cents = Number(tx.amount_cents || 0);
    rows.push({
      ts: parseTs(tx.created_at),
      source: 'Guthaben',
      label: `${cents > 0 ? '+' : '−'}${fmtEuroCents(Math.abs(cents))} — ${WALLET_KIND_LABELS[tx.kind] || tx.kind}`,
      detail: tx.description || null,
    });
  }
  return rows.sort((left, right) => left.ts - right.ts);
}

function PaymentFilePanel({ rideId, onBack }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(null);
  const objectUrls = useRef([]);

  useEffect(() => {
    let current = true;
    setFile(null);
    setLoading(true);
    setError('');
    api.get(`/admin/rides/${encodeURIComponent(rideId)}/payment-file`)
      .then(r => { if (current) { setFile(r.data); setLoading(false); } })
      .catch(err => {
        if (!current) return;
        setError(err?.response?.status === 404 ? 'Fahrt nicht gefunden.' : 'Zahlungsakte konnte nicht geladen werden.');
        setLoading(false);
      });
    return () => { current = false; };
  }, [rideId]);
  useEffect(() => () => { objectUrls.current.forEach((url) => window.URL.revokeObjectURL(url)); }, []);

  // Beleg-Download über den bestehenden Weg /finance/documents/:id (Zugriffsprotokoll läuft dort).
  const openDocument = async (doc) => {
    if (!doc.available) {
      toast.error(`Beleg ${doc.document_number} ist nicht freigegeben (${doc.blocked_code || 'gesperrt'})`);
      return;
    }
    setOpening(doc.id);
    try {
      const response = await api.get(`/finance/documents/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      objectUrls.current.push(url);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error('Beleg konnte nicht geöffnet werden');
    } finally { setOpening(null); }
  };

  if (loading) return <div className="users-modal-loading"><Skeleton h={150} r={12} /></div>;
  if (error || !file) {
    return (
      <div className="users-payment-file">
        <button className="users-payment-back" onClick={onBack}><ArrowLeft size={13} /> Zurück zum Verlauf</button>
        <div className="users-modal-error"><span>{error || 'Zahlungsakte konnte nicht geladen werden.'}</span></div>
      </div>
    );
  }

  const ride = file.ride;
  const standing = paymentStanding({ ...ride, attempt_state: file.attempt?.state || null });
  const errorCode = file.attempt?.last_error_code || null;
  const walletName = walletMethodLabel(file.card?.wallet_type);
  const timeline = paymentFileTimeline(file);

  return (
    <div className="users-payment-file">
      <button className="users-payment-back" onClick={onBack}><ArrowLeft size={13} /> Zurück zum Verlauf</button>

      <section className="users-payment-summary">
        <header>
          <div>
            <span className="users-overline">Zahlungsakte</span>
            <h3>Fahrt {String(ride.id).slice(0, 8)} · {fmtDbDate(ride.completed_at || ride.created_at, true)}</h3>
          </div>
          <span className={`users-payment-standing is-${standing.tone}`}><i /> {standing.label}</span>
        </header>
        <div className="users-payment-facts">
          <div><small>Soll</small><strong>{fmtEuro(ride.status === 'cancelled' ? ride.cancellation_fee : (ride.customer_due ?? ride.price))}</strong></div>
          <div>
            <small>Zahlart</small>
            <strong>
              {ride.payment_method === 'cash' ? 'Bar' : walletName ? `${walletName} (Karte)` : 'Karte'}
              {file.card ? ` · ${file.card.brand || 'Karte'} •••• ${file.card.last4 || '????'}` : ''}
            </strong>
          </div>
          <div><small>Aufteilung</small><strong>{splitSummary(ride) || '—'}</strong></div>
          {file.attempt && (
            <div><small>Karten-Vorgang</small><strong>{file.attempt.state}{errorCode ? ` · ${paymentErrorLabel(errorCode)}` : ''}</strong></div>
          )}
          {file.attempt?.provider_intent_id && (
            <div><small>Stripe-Referenz</small><strong className="users-payment-ref">{file.attempt.provider_intent_id}</strong></div>
          )}
          {file.attempt?.superseded_provider_intent_id && (
            <div>
              <small>Ersetzte Autorisierung (Re-Autorisierung)</small>
              <strong className="users-payment-ref">
                {file.attempt.superseded_provider_intent_id}
                {` · Freigabe ${file.attempt.superseded_authorization_released ? 'erfolgt' : 'ausstehend'}`}
              </strong>
            </div>
          )}
          {file.authorization_release && (
            <div>
              <small>Freigabe-Outbox</small>
              <strong>
                {file.authorization_release.status}
                {file.authorization_release.last_error_code ? ` · ${paymentErrorLabel(file.authorization_release.last_error_code)}` : ''}
              </strong>
            </div>
          )}
          {file.stripe_fee && (
            <div><small>Stripe-Gebühr</small><strong>{fmtEuroCents(file.stripe_fee.fee_cents)}</strong></div>
          )}
          {file.outbox && (
            <div><small>Einzugs-Outbox</small><strong>{file.outbox.operation} · {file.outbox.status}{file.outbox.last_error_code ? ` · ${paymentErrorLabel(file.outbox.last_error_code)}` : ''}</strong></div>
          )}
        </div>
        <p className="users-payment-route"><MapPin size={12} /> {ride.pickup_address || '—'} → {ride.dropoff_address || '—'}</p>
      </section>

      <section className="users-payment-timeline">
        <header><span className="users-overline">Verlauf</span><h4>Was ist wann passiert?</h4></header>
        {timeline.length === 0 ? (
          <Empty icon={History} title="Keine Ereignisse aufgezeichnet" />
        ) : (
          <ol>
            {timeline.map((row, index) => (
              <li key={index}>
                <span className="users-payment-timeline-ts">{row.ts ? fmtDate(row.ts, true) : '—'}</span>
                <span className={`users-payment-timeline-source is-${row.source.toLowerCase()}`}>{row.source}</span>
                <div>
                  <strong>{row.label}</strong>
                  {row.detail && <small>{row.detail}</small>}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {(file.tips || []).length > 0 && (
        <section className="users-payment-tips">
          <header><span className="users-overline">Trinkgeld-Versuche</span></header>
          <ul>
            {file.tips.map(tip => (
              <li key={tip.id}>
                {fmtEuroCents(tip.amount_cents)} · {tip.state}
                {tip.last_error_code ? ` · ${paymentErrorLabel(tip.last_error_code)}` : ''}
                <small>{fmtDbDate(tip.created_at, true)}</small>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="users-payment-documents">
        <header><span className="users-overline">Belege</span><h4>Dasselbe Dokument wie beim Kunden</h4></header>
        {(file.documents || []).length === 0 ? (
          <p className="users-payment-nodocs">{file.message || 'Für diese Fahrt existiert kein Beleg.'}</p>
        ) : (
          <ul>
            {file.documents.map(doc => (
              <li key={doc.id}>
                <button onClick={() => openDocument(doc)} disabled={opening === doc.id}>
                  <FileText size={13} /> {doc.kind_label} {doc.document_number}
                  {doc.amount != null ? ` · ${fmtEuro(doc.amount)}` : ''}
                  {opening === doc.id ? ' …' : ''}
                </button>
                {!doc.available && <small>gesperrt: {doc.blocked_code}</small>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function FeeWaiver({ userId, item, onDone }) {
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const { can } = useAuth();
  if (!can('finance.manage') || item.kind !== 'cancellation_fee' || item.status !== 'open') return null;
  if (!item.waiver?.allowed) return item.waiver?.reason ? <small>{item.waiver.reason}</small> : null;
  const submit = async event => {
    event.preventDefault();
    if (inFlight.current || !confirmed || reason.trim().length < 10) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      await api.post(`/admin/users/${encodeURIComponent(userId)}/cancellation-fees/${item.wallet_tx_id}/waive`, {
        reason: reason.trim(), confirm: true, expected_amount_cents: item.waiver.amount_cents,
      });
      await onDone();
      setExpanded(false); setReason(''); setConfirmed(false);
    } catch (err) {
      setError(err?.response?.data?.error || 'Ergebnis noch nicht bestätigt. Bitte neu laden oder dieselbe Bestätigung erneut senden.');
    } finally { inFlight.current = false; setBusy(false); }
  };
  return <div className="users-fee-waiver">
    {!expanded ? <button onClick={() => setExpanded(true)}>Stornogebühr erlassen</button> : (
      <form onSubmit={submit}>
        <p>Offen: {fmtEuroCents(item.waiver.amount_cents)}. Fahrerumsatz: {fmtEuroCents(item.waiver.driver_delta_cents)}.
          Partnerkonto: {fmtEuroCents(item.waiver.partner_delta_cents)}. Die Korrektur wirkt sofort und erscheint einmal in der nächsten Abrechnung.</p>
        <label>Grund für den Erlass<textarea aria-label="Grund für den Erlass" minLength={10} maxLength={1000}
          required value={reason} disabled={busy} onChange={event => setReason(event.target.value)} /></label>
        <label><input type="checkbox" checked={confirmed} disabled={busy} onChange={event => setConfirmed(event.target.checked)} />
          Diese Stornogebühr mit der angezeigten Korrektur verbindlich erlassen</label>
        {error && <p role="alert">{error}</p>}
        <button type="submit" disabled={busy || !confirmed || reason.trim().length < 10}>{busy ? 'Wird gebucht …' : 'Erlass bestätigen'}</button>
        <button type="button" disabled={busy} onClick={() => { setExpanded(false); setConfirmed(false); setError(''); }}>Abbrechen</button>
      </form>
    )}
  </div>;
}

export function PaymentsPanel({ userId }) {
  const currentUser = useRef(userId);
  currentUser.current = userId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fileRideId, setFileRideId] = useState(null);
  // REVIEW-FIX-002: hinter der Trunkierung nachladen — ältere Fahrten bleiben erreichbar.
  const [extraRides, setExtraRides] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    api.get(`/admin/users/${encodeURIComponent(userId)}/payments`)
      .then(r => { if (current) { setData(r.data); setLoading(false); } })
      .catch(err => {
        if (!current) return;
        setError(err?.response?.status === 403
          ? 'Dafür fehlt die Finanz-Berechtigung (finance.manage).'
          : 'Zahlungsverlauf konnte nicht geladen werden.');
        setLoading(false);
      });
    return () => { current = false; };
  }, [userId]);

  const allRides = [...((data && data.rides) || []), ...extraRides];
  const totalRides = Number(data?.total || 0);
  const hasMoreRides = totalRides > allRides.length;

  const loadMoreRides = async () => {
    if (loadingMore || !hasMoreRides) return;
    setLoadingMore(true);
    try {
      const r = await api.get(`/admin/users/${encodeURIComponent(userId)}/payments?offset=${allRides.length}`);
      setExtraRides(previous => [...previous, ...(r.data.rides || [])]);
    } catch {
      toast.error('Weitere Zahlungen konnten nicht geladen werden');
    } finally { setLoadingMore(false); }
  };

  if (fileRideId) return <PaymentFilePanel rideId={fileRideId} onBack={() => setFileRideId(null)} />;
  if (loading) return <div className="users-modal-loading"><Skeleton h={150} r={12} /></div>;
  if (error || !data) return <div className="users-modal-error"><span>{error || 'Zahlungsverlauf konnte nicht geladen werden.'}</span></div>;

  const openFees = data.open_fees || {};
  const feeItems = data.fee_items || [];

  return (
    <div className="users-payments">
      <section className="users-payments-meta">
        <div>
          <small>VOOGO Guthaben</small>
          <strong className={data.wallet?.balance_cents < 0 ? 'is-negative' : ''}>
            {fmtEuroCents(data.wallet?.balance_cents || 0)}
          </strong>
        </div>
        <div>
          {/* STRIPE-AUFSTOCKUNG-ADMIN-001: Fahrpreisreste zählen mit und heißen beim Namen. */}
          <small>{openFees.shortfall_count > 0 ? (openFees.fee_count > 0 ? 'Offene Beträge (Storno + Fahrpreisrest)' : 'Offene Fahrpreisreste') : 'Offene Stornogebühren'}</small>
          <strong className={openFees.cents > 0 ? 'is-negative' : ''}>
            {openFees.count || 0} · {fmtEuroCents(openFees.cents || 0)}
          </strong>
        </div>
        <div>
          <small>Zahlungen</small>
          <strong>{totalRides}{hasMoreRides ? ` (${allRides.length} geladen)` : ''}</strong>
        </div>
      </section>

      <section className="users-payments-list">
        <header>
          <div>
            <span className="users-overline">Zahlungsverlauf</span>
            <h3>Fahrten &amp; Gebühren</h3>
          </div>
        </header>
        {allRides.length === 0 ? (
          <Empty icon={Wallet} title="Noch keine abgeschlossenen Zahlungen" />
        ) : (
          <div className="users-payments-rows">
            {allRides.map(ride => {
              const standing = paymentStanding(ride);
              const errorCode = ride.outbox_error_code || ride.attempt_error_code;
              return (
                <button key={ride.id} className="users-payments-row" onClick={() => setFileRideId(ride.id)}>
                  <div className="users-payments-row-main">
                    <strong>{fmtEuro(ride.status === 'cancelled' ? ride.cancellation_fee : (ride.customer_due ?? ride.price))}</strong>
                    <span>{ride.payment_method === 'cash' ? <Banknote size={13} /> : <CreditCard size={13} />} {ride.payment_method === 'cash' ? 'Bar' : 'Karte'}</span>
                    <small>{fmtDbDate(ride.completed_at || ride.created_at, true)}</small>
                  </div>
                  <div className="users-payments-row-context">
                    <span className={`users-payment-standing is-${standing.tone}`}><i /> {standing.label}</span>
                    {errorCode && <small>{paymentErrorLabel(errorCode)}</small>}
                    <small>{ride.dropoff_address?.substring(0, 40) || '—'}</small>
                  </div>
                  <ArrowRight size={13} />
                </button>
              );
            })}
          </div>
        )}
        {hasMoreRides && (
          <button className="users-payments-more" onClick={loadMoreRides} disabled={loadingMore}>
            {loadingMore ? 'Lädt …' : `Mehr anzeigen (${totalRides - allRides.length} weitere)`}
          </button>
        )}
      </section>

      {feeItems.length > 0 && (
        <section className="users-payments-fees">
          <header><span className="users-overline">{feeItems.some((item) => item.kind === 'fare_shortfall') ? 'Offene Posten (Stornogebühren und Fahrpreisreste)' : 'Stornogebühren-Einzelposten'}</span></header>
          <ul>
            {feeItems.map(item => (
              <li key={item.wallet_tx_id} className={item.status === 'open' ? 'is-open' : 'is-settled'}>
                <strong>{fmtEuroCents(item.amount_cents)}</strong>
                <span>{item.kind === 'fare_shortfall' ? 'Fahrpreisrest · ' : ''}{item.status === 'open' ? 'offen' : item.status === 'waived' ? 'erlassen' : 'beglichen'}</span>
                <small>
                  Fahrt {item.ride_id ? String(item.ride_id).slice(0, 8) : '—'} · {fmtDbDate(item.created_at, true)}
                  {item.status === 'settled' && item.settled_via && (
                    ` · beglichen ${fmtDbDate(item.settled_at, true)} (${WALLET_KIND_LABELS[item.settled_via.kind] || (item.settled_via.kind === 'covered_by_balance' ? 'durch Guthaben gedeckt' : item.settled_via.kind)})`
                  )}
                </small>
                <FeeWaiver key={`${userId}:${item.wallet_tx_id}`} userId={userId} item={item} onDone={async () => {
                  if (currentUser.current !== userId) return;
                  const response = await api.get(`/admin/users/${encodeURIComponent(userId)}/payments`);
                  if (currentUser.current !== userId) return;
                  setData(response.data); setExtraRides([]);
                }} />
                {item.ride_id && (
                  <button onClick={() => setFileRideId(item.ride_id)}>Akte <ArrowRight size={11} /></button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {(data.fee_settlements || []).length > 0 && (
        <section className="users-payments-settlements">
          <header><span className="users-overline">Karten-Begleichungen offener Gebühren</span></header>
          <ul>
            {data.fee_settlements.map(settlement => (
              <li key={settlement.id} className={`is-${settlement.state}`}>
                <strong>{fmtEuroCents(settlement.amount_cents)}</strong>
                <span>
                  {settlement.state === 'succeeded' ? 'erfolgreich'
                    : settlement.state === 'failed' ? `fehlgeschlagen${settlement.last_error_code ? ` — ${paymentErrorLabel(settlement.last_error_code)}` : ''}`
                      : 'läuft'}
                </span>
                <small>
                  {settlement.trigger_kind === 'ride_completion' ? 'automatisch nach Kartenfahrt' : 'vom Kunden gestartet'}
                  {` · ${fmtDbDate(settlement.created_at, true)}`}
                  {settlement.provider_intent_id ? ` · ${settlement.provider_intent_id}` : ''}
                </small>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="users-payments-wallet">
        <header><span className="users-overline">Guthaben-Buchungen (letzte 50)</span></header>
        {(data.wallet?.transactions || []).length === 0 ? (
          <p className="users-payment-nodocs">Keine Buchungen.</p>
        ) : (
          <ul>
            {data.wallet.transactions.map(tx => (
              <li key={tx.id}>
                <strong className={tx.amount_cents < 0 ? 'is-negative' : 'is-positive'}>
                  {tx.amount_cents > 0 ? '+' : '−'}{fmtEuroCents(Math.abs(tx.amount_cents))}
                </strong>
                <span>{WALLET_KIND_LABELS[tx.kind] || tx.kind}</span>
                <small>{tx.description || ''} · {fmtDbDate(tx.created_at, true)}</small>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ── KUNDEN-GUTHABEN-VERLAUF-001 (Owner 06.10.2026): Aktionsguthaben-Akte je Kunde ─────────────
// Das Aktionsguthaben (promo_credits: Werbeprämien, Miles, Admin-Gutschriften, Einlösungen,
// Rückbuchungen) ist ein GETRENNTES Buch vom echten Wallet im Reiter „Zahlungen". Bisher war
// es im Admin unsichtbar — eine Fahrt zeigte „−6,20 € Guthaben", ohne dass irgendwo stand, woher.
// Dieser Reiter zeigt alles aus einer Antwort: Werbungen, Miles, Gutschriften, kompletter Verlauf.
const PROMO_KIND_LABELS = {
  referral: 'Werbeprämie',
  miles: 'Miles-Gutschrift',
  adjustment: 'Gutschrift (Erstattung/Entschädigung)',
  bonus: 'Bonus',
  redeem: 'Eingelöst für Fahrt',
  refund: 'Rückbuchung (Storno/Routenänderung)',
};
export const promoKindLabel = (kind) => PROMO_KIND_LABELS[kind] || kind;
const shortRide = (rideId) => (rideId ? String(rideId).slice(0, 8) : '');
const fmtKm = (km) => `${Number(km || 0).toLocaleString('de-DE', { maximumFractionDigits: 1 })} km`;
const signedCents = (cents) => `${cents < 0 ? '−' : '+'}${fmtEuroCents(Math.abs(cents))}`;

export function PromoCreditsPanel({ userId, refreshToken = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    api.get(`/admin/users/${encodeURIComponent(userId)}/promo-credits`)
      .then(r => { if (current) { setData(r.data); setLoading(false); } })
      .catch(err => {
        if (!current) return;
        setError(err?.response?.status === 403
          ? 'Dafür fehlt die Finanz-Berechtigung (finance.manage).'
          : 'Guthaben-Akte konnte nicht geladen werden.');
        setLoading(false);
      });
    return () => { current = false; };
  }, [userId, refreshToken]);

  if (loading) return <div className="users-modal-loading"><Skeleton h={150} r={12} /></div>;
  if (error || !data) return <div className="users-modal-error"><span>{error || 'Guthaben-Akte konnte nicht geladen werden.'}</span></div>;

  const summary = data.summary || {};
  const byKind = summary.by_kind || {};
  const kind = (name) => byKind[name] || { count: 0, cents: 0 };
  const ref = data.referral || {};
  const miles = data.miles || {};
  const adminCredits = data.admin_credits || [];
  // Neueste Buchung oben; der Server liefert Buchreihenfolge (alt → neu) mit laufendem Saldo.
  const entries = [...(data.entries || [])].reverse();
  const handCount = kind('adjustment').count + kind('bonus').count;
  const handCents = kind('adjustment').cents + kind('bonus').cents;

  return (
    <div className="users-payments users-promo">
      <section className="users-payments-meta">
        <div>
          <small>Aktionsguthaben aktuell</small>
          <strong>{fmtEuroCents(data.balance_cents || 0)}</strong>
        </div>
        <div>
          <small>Gutschriften (Erstattung/Bonus)</small>
          <strong>{handCount} · {fmtEuroCents(handCents)}</strong>
        </div>
        <div>
          <small>Werbeprämien erhalten</small>
          <strong>{kind('referral').count} · {fmtEuroCents(kind('referral').cents)}</strong>
        </div>
        <div>
          <small>Miles gutgeschrieben</small>
          <strong>{fmtEuroCents(miles.credited_total_cents || 0)} · {fmtKm(miles.credited_km)}</strong>
        </div>
        <div>
          <small>Für Fahrten verbraucht (netto)</small>
          <strong>{fmtEuroCents(summary.used_net_cents || 0)}</strong>
        </div>
      </section>
      {data.consistent === false && (
        <p className="users-promo-warning">Lauf-Saldo und Buchsaldo weichen ab — bitte Finanzprüfung.</p>
      )}

      <section className="users-payments-wallet users-promo-section">
        <header><span className="users-overline">Werbungen (Einladungen)</span></header>
        <dl className="users-promo-facts">
          <div><dt>Eingeladen</dt><dd>{ref.invites_sent || 0}</dd></div>
          <div><dt>Registriert</dt><dd>{ref.invites_registered || 0}</dd></div>
          <div><dt>Belohnt</dt><dd>{ref.invites_rewarded || 0}</dd></div>
          <div><dt>Prämien als Werber</dt><dd>{ref.earned_as_referrer_count || 0} · {fmtEuroCents(ref.earned_as_referrer_cents || 0)}</dd></div>
        </dl>
        {ref.referred_by && (
          <p className="users-promo-note">
            Selbst geworben von <strong>{ref.referred_by.name || 'unbekanntem Konto'}</strong>
            {ref.received_as_referred
              ? ` · Willkommensbonus ${fmtEuroCents(ref.received_as_referred.cents)} am ${fmtDbDate(ref.received_as_referred.created_at)}`
              : ' · noch kein Willkommensbonus (erste Fahrt steht aus)'}
          </p>
        )}
        {(ref.invites || []).length === 0 ? (
          <p className="users-payment-nodocs">Keine Einladungen verschickt.</p>
        ) : (
          <ul>
            {ref.invites.map(invite => (
              <li key={invite.id}>
                <strong className={invite.reward_cents ? 'is-positive' : ''}>
                  {invite.reward_cents ? `+${fmtEuroCents(invite.reward_cents)}` : '—'}
                </strong>
                <span>{invite.phone_masked} · {invite.status}{invite.redeemed_name ? ` · ${invite.redeemed_name}` : ''}</span>
                <small>eingeladen {fmtDbDate(invite.created_at)}{invite.rewarded_at ? ` · belohnt ${fmtDbDate(invite.rewarded_at)}` : ''}</small>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="users-payments-wallet users-promo-section">
        <header><span className="users-overline">Miles{miles.program_live === false ? ' (Programm aus)' : ''}</span></header>
        <dl className="users-promo-facts">
          <div>
            <dt>Gutgeschrieben</dt>
            <dd>{fmtEuroCents(miles.credited_total_cents || 0)} für {fmtKm(miles.credited_km)} · {miles.months_credited || 0} Monat{(miles.months_credited || 0) === 1 ? '' : 'e'}</dd>
          </div>
          {miles.current_month && (
            <div>
              <dt>Laufender Monat {miles.current_month.year_month}</dt>
              <dd>{fmtKm(miles.current_month.total_km)} gefahren · {fmtKm(miles.current_month.pending_km)} offen · {fmtEuroCents(miles.current_month.redeemable_cents)} einlösbar</dd>
            </div>
          )}
          {miles.previous_month && (
            <div>
              <dt>Vormonat {miles.previous_month.year_month}</dt>
              <dd>{fmtKm(miles.previous_month.total_km)} gefahren · {fmtEuroCents(miles.previous_month.credited_cents)} gutgeschrieben</dd>
            </div>
          )}
        </dl>
        <p className="users-promo-note">Verbrauch für Fahrten wird für das gesamte Aktionsguthaben angezeigt; der Anteil aus Miles ist nicht getrennt erfasst.</p>
        {(miles.months || []).length === 0 ? (
          <p className="users-payment-nodocs">Noch keine Miles-Gutschrift.</p>
        ) : (
          <ul>
            {miles.months.map(month => (
              <li key={month.year_month}>
                <strong className={month.dry_run ? '' : 'is-positive'}>{month.dry_run ? '—' : `+${fmtEuroCents(month.credited_cents)}`}</strong>
                <span>{month.year_month} · {fmtKm(month.km)}{month.dry_run ? ' · Trockenlauf (kein Guthaben)' : ''}</span>
                <small>{fmtDbDate(month.created_at, true)}</small>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="users-payments-wallet users-promo-section">
        <header><span className="users-overline">Gutschriften von Hand (Erstattung/Entschädigung)</span></header>
        {adminCredits.length === 0 ? (
          <p className="users-payment-nodocs">Keine Admin-Gutschriften.</p>
        ) : (
          <ul>
            {adminCredits.map((credit, index) => (
              <li key={`${credit.created_at}-${index}`}>
                <strong className="is-positive">+{fmtEuroCents(credit.amount_cents)}</strong>
                <span>{credit.reason}{credit.ride_id ? ` · Fahrt ${shortRide(credit.ride_id)}` : ''}</span>
                <small>{credit.actor_name ? `von ${credit.actor_name}` : 'Admin unbekannt'} · {fmtDbDate(credit.created_at, true)}</small>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="users-payments-wallet users-promo-section">
        <header><span className="users-overline">Verlauf — alle Buchungen ({entries.length})</span></header>
        {entries.length === 0 ? (
          <p className="users-payment-nodocs">Noch keine Guthaben-Buchung.</p>
        ) : (
          <table className="users-promo-table">
            <thead>
              <tr><th>Datum</th><th>Vorgang</th><th>Fahrt</th><th>Betrag</th><th>Saldo danach</th></tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id}>
                  <td>{fmtDbDate(entry.created_at, true)}</td>
                  <td>
                    <strong>{promoKindLabel(entry.kind)}</strong>
                    {entry.description ? <small>{entry.description}</small> : null}
                    {entry.actor_name ? <small>von {entry.actor_name}</small> : entry.kind === 'adjustment' ? (
                      <small>{entry.admin_attribution === 'ambiguous'
                        ? 'Herkunft uneindeutig (Admin/Fahrt)' : 'Admin unbekannt'}</small>
                    ) : null}
                  </td>
                  <td>
                    {entry.ride_id ? (
                      <>
                        {shortRide(entry.ride_id)}
                        {entry.ride_status ? (
                          <small>{statusLabels[entry.ride_status] || entry.ride_status}{entry.ride_price != null ? ` · ${fmtEuro(entry.ride_price)}` : ''}</small>
                        ) : null}
                      </>
                    ) : '—'}
                  </td>
                  <td className={entry.amount_cents < 0 ? 'is-negative' : 'is-positive'}>{signedCents(entry.amount_cents)}</td>
                  <td>{fmtEuroCents(entry.balance_after_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

// KORREKTURBUCHUNG-ADMIN-001: Kundenentschädigung als Aktionsguthaben (zentraler,
// auditierter Weg, kind 'adjustment'). Der Kunde sieht EIN Guthaben; es bezahlt die
// nächste Fahrt zuerst. Sichtbar nur mit finance.manage; der Server erzwingt dasselbe Recht.
// REVIEW-FIX R3-P3: Betrag STRIKT parsen — höchstens zwei Nachkommastellen, sonst ablehnen.
// Vorher wurde 1,005 still zu 100 Cent gerundet; stilles Runden ist bei Geld ein Fehlerbild.
const parseEuroCents = (raw) => {
  const value = String(raw).trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(value)) return null;
  const cents = Math.round(Number(value) * 100);
  return cents > 0 ? cents : null;
};

// Idempotenz-Schlüssel je Formular-Anlauf (REVIEW-FIX P1-3): die Route verlangt ihn jetzt,
// damit Doppelklick/Retry nie doppelt gutschreibt. Fallback für Testumgebungen ohne crypto.
const newCreditKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `gutschrift-${Date.now()}-${Math.random().toString(16).slice(2)}`);

function UserCreditCard({ userId, userRole, onGranted }) {
  const { can } = useAuth();
  const allowed = Boolean(can && can('finance.manage'));
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [balance, setBalance] = useState(null);
  const idempotencyRef = useRef(newCreditKey());
  if (!allowed || (userRole && userRole !== 'customer')) return null;

  const submit = async (event) => {
    event.preventDefault();
    const parsed = parseEuroCents(amount);
    if (parsed === null) { toast.error('Betrag in Euro mit höchstens zwei Nachkommastellen angeben, z. B. 5,00'); return; }
    if (!reason.trim()) { toast.error('Grund ist Pflicht'); return; }
    setSaving(true);
    try {
      const response = await api.post(`/admin/users/${userId}/promo-credit`, {
        amount_cents: parsed,
        reason: reason.trim(),
      }, { headers: { 'Idempotency-Key': idempotencyRef.current } });
      const newBalance = Number(response.data?.promo_balance || 0);
      idempotencyRef.current = newCreditKey();
      setBalance(newBalance);
      setAmount('');
      setReason('');
      toast.success(`Gutgeschrieben — Aktionsguthaben jetzt ${newBalance.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`);
      // Guthaben-Akte nach erfolgreicher Gutschrift neu laden.
      if (typeof onGranted === 'function') onGranted();
    } catch (requestError) {
      toast.error(requestError.response?.data?.message || 'Gutschrift abgelehnt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="users-credit-card" aria-label="Guthaben-Gutschrift">
      <span className="users-overline">Guthaben-Gutschrift (Entschädigung)</span>
      <p>Schreibt Aktionsguthaben mit protokolliertem Grund gut — es bezahlt die nächste Fahrt zuerst.{balance !== null && ` Aktionsguthaben aktuell: ${balance.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €.`}</p>
      <form onSubmit={submit}>
        <input
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          placeholder="Betrag in €, z. B. 5,00"
          inputMode="decimal"
          aria-label="Betrag in Euro"
        />
        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Grund (Pflicht), z. B. Entschädigung Wartezeit"
          aria-label="Grund"
        />
        <button type="submit" disabled={saving}>{saving ? 'Bucht…' : 'Gutschreiben'}</button>
      </form>
    </section>
  );
}

function UserDetailModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryToken, setRetryToken] = useState(0);
  // KUNDEN-ZAHLUNGSAKTE-001: zweiter Reiter „Zahlungen" — kundenspezifisch, keine globale Liste.
  const [tab, setTab] = useState('rides');
  const [creditVersion, setCreditVersion] = useState(0);

  useEffect(() => {
    let current = true;
    setData(null);
    setLoading(true);
    api.get(`/admin/users/${userId}/rides`)
      .then(r => {
        if (!current) return;
        setData(r.data);
        setLoading(false);
      })
      .catch(() => {
        if (current) setLoading(false);
      });
    return () => { current = false; };
  }, [retryToken, userId]);

  return (
    <div className="users-modal-backdrop" role="dialog" aria-modal="true" aria-label="Kunden-Profil">
      <div className="users-modal">
        <header>
          <div>
            {/* KONTO-TRENNUNG-001: die Kontoart steht im Kopf — nicht nur im Titel, sondern als
                eigene Zeile, damit bei gleichem Namen/E-Mail nie unklar ist, welches Konto offen ist. */}
            <span className="users-overline">{data?.user ? accountKindLabel(data.user.role) : 'Kontokontext'}</span>
            <h2>Kunden-Profil</h2>
          </div>
          <button onClick={onClose} aria-label="Kunden-Profil schließen"><X size={16} /></button>
        </header>

        {loading ? (
          <div className="users-modal-loading"><Skeleton h={150} r={12} /></div>
        ) : data ? (
          <div className="users-modal-content">
            <section className="users-profile-card">
              <div className="users-profile-avatar">{initials(data.user?.name)}</div>
              <div className="users-profile-copy">
                <h3>{data.user?.name || 'Unbenanntes Konto'}</h3>
                <p>{data.user?.email || 'Keine E-Mail'}</p>
                {data.user?.phone && <p>{data.user.phone}</p>}
                <div className="users-profile-meta">
                  <span className={`users-role is-${data.user?.role}`}>{roleLabels[data.user?.role] || data.user?.role}</span>
                  <span><Star size={12} /> {parseFloat(data.user?.rating || 5).toFixed(1)}</span>
                  <span><Car size={12} /> {data.rides.length} Fahrten</span>
                  <span className={`is-${data.user?.status || 'active'}`}>
                    <i /> {accountStatusLabels[data.user?.status] || data.user?.status || 'Aktiv'}
                  </span>
                  <span><Clock3 size={12} /> seit {fmtDate(data.user?.created_at)}</span>
                </div>
              </div>
            </section>

            <UserCreditCard userId={userId} userRole={data.user?.role} onGranted={() => setCreditVersion(value => value + 1)} />
            {data.user?.role==='customer' && <AccountProtection key={`kontoschutz-${userId}`} userId={userId} />}

            <nav className="users-detail-tabs" aria-label="Kundenprofil-Bereiche">
              <button className={tab === 'rides' ? 'is-active' : ''} onClick={() => setTab('rides')}>
                <Car size={13} /> Fahrten
              </button>
              <button className={tab === 'payments' ? 'is-active' : ''} onClick={() => setTab('payments')}>
                <Wallet size={13} /> Zahlungen
              </button>
              <button className={tab === 'credits' ? 'is-active' : ''} onClick={() => setTab('credits')}>
                <Gift size={13} /> Guthaben &amp; Prämien
              </button>
            </nav>

            {tab === 'credits' ? (
              <PromoCreditsPanel key={`guthaben-${userId}`} userId={userId} refreshToken={creditVersion} />
            ) : tab === 'payments' ? (
              // REVIEW-FIX-001 (P2): key erzwingt einen frischen Panel-Zustand je Kunde — sonst
              // bliebe beim Kundenwechsel (⌘K: /users?detail=<id>) die geöffnete Akte des
              // VORHERIGEN Kunden stehen.
              <>
                {/* ZAHLART-SPERRE-KUNDE-001: Zahlart-Schalter je Kunde (nur Kundenkonten, finance.manage). */}
                {data.user?.role === 'customer' && <UserPaymentMethods key={`zahlarten-${userId}`} userId={userId} />}
                <PaymentsPanel key={userId} userId={userId} />
              </>
            ) : (
            <section className="users-history">
              <header>
                <div>
                  <span className="users-overline">Letzte 50 Datensätze</span>
                  <h3>Fahrthistorie</h3>
                </div>
                <strong>{data.rides.length}</strong>
              </header>

              {data.rides.length === 0 ? (
                <Empty icon={Car} title="Noch keine Fahrten" />
              ) : (
                <div className="users-history-list">
                  {data.rides.map(ride => (
                    <article key={ride.id}>
                      <div className="users-history-route">
                        <span><i /> {ride.pickup_address?.substring(0, 42) || 'Unbekannte Abholung'}</span>
                        <span><i /> {ride.dropoff_address?.substring(0, 34) || 'Unbekanntes Ziel'}</span>
                      </div>
                      <div className="users-history-context">
                        <span>{ride.driver_name || 'Kein Fahrer'}</span>
                        <span>{fmtDate(ride.created_at)}</span>
                        <span>{ride.payment_method === 'cash' ? <Banknote size={13} /> : <CreditCard size={13} />}</span>
                      </div>
                      <div className="users-history-price">
                        <strong>{Number(ride.price || 0).toFixed(2)} €</strong>
                        <span style={{ '--user-ride-status': statusColors[ride.status] || '#6b7280' }}>
                          <i /> {statusLabels[ride.status] || ride.status}
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
            )}
          </div>
        ) : (
          <div className="users-modal-error">
            <span>Kunden-Profil konnte nicht geladen werden.</span>
            <button onClick={() => setRetryToken(value => value + 1)}>
              <RefreshCw size={12} /> Erneut versuchen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Users() {
  const [mode, setMode] = useState('directory');
  const [totalCustomers, setTotalCustomers] = useState(null);
  const [recentRides, setRecentRides] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [contextDraft, setContextDraft] = useState('');
  const [contextSearch, setContextSearch] = useState('');
  const [recentStatus, setRecentStatus] = useState('');

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(false);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLimit, setUsersLimit] = useState(DIRECTORY_LIMIT);
  const [usersOffset, setUsersOffset] = useState(0);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('customer');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [securityUser, setSecurityUser] = useState(null);
  const [canManageAccountSecurity, setCanManageAccountSecurity] = useState(false);
  const securityTrigger = useRef(null);
  const usersRequest = useRef(0);
  const loadUsersRef = useRef(null);
  const location = useLocation();
  // Seitengröße 25/50/100 (Owner-Entscheid 26.08.; Server clampt auf max. 100, Default 25).
  const [pageSize, setPageSize] = useState(DIRECTORY_LIMIT);

  // ⌘K-Palette: /users?detail=<id> öffnet das vorhandene Kundenprofil-Modal (auch bei
  // erneuter Navigation auf derselben Seite); /users?browse=1 springt in den
  // Verzeichnis-Modus („Konten durchsuchen"). Ohne Parameter ändert sich nichts.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // AKTE-LINKS-FAHRT-DETAIL-001: IDs sind TEXT (UUID); die frühere Ganzzahl-Prüfung ließ jeden echten
    // Deep-Link ins Leere laufen. Erlaubt: 1–64 Zeichen aus [A-Za-z0-9_-].
    const raw = params.get('detail');
    if (raw && /^[A-Za-z0-9_-]{1,64}$/.test(raw)) setDetailId(raw);
    if (params.get('browse')) setMode('directory');
  }, [location]);

  useEffect(() => {
    api.get('/admin/dashboard')
      .then(response => setTotalCustomers(response.data.stats?.total_customers ?? null))
      .catch(() => setTotalCustomers(null));
  }, []);

  const loadRecent = useCallback(() => {
    setRecentLoading(true);
    const params = new URLSearchParams();
    if (contextSearch) params.set('search', contextSearch);
    if (recentStatus) params.set('status', recentStatus);
    params.set('limit', '50');
    api.get(`/admin/rides?${params}`)
      .then(r => setRecentRides(r.data.rides || []))
      .catch(() => toast.error('Kundenaktivität konnte nicht geladen werden'))
      .finally(() => setRecentLoading(false));
  }, [contextSearch, recentStatus]);

  useEffect(() => {
    if (mode === 'recent') loadRecent();
  }, [mode, loadRecent]);

  const loadUsers = useCallback(() => {
    const requestId = usersRequest.current + 1;
    usersRequest.current = requestId;
    setUsersLoading(true);
    setUsersError(false);
    setCanManageAccountSecurity(false);
    const params = new URLSearchParams();
    if (roleFilter) params.set('role', roleFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (fromFilter) params.set('from', fromFilter);
    if (toFilter) params.set('to', toFilter);
    if (search) params.set('search', search);
    params.set('limit', String(pageSize));
    params.set('offset', String(usersOffset));
    api.get(`/admin/users?${params}`)
      .then(r => {
        if (requestId !== usersRequest.current) return;
        const nextUsers = Array.isArray(r.data.users) ? r.data.users : [];
        const nextTotal = Math.max(0, Number(r.data.total) || 0);
        const nextLimit = Math.min(pageSize, Number(r.data.limit) || pageSize);
        const nextOffset = Math.max(0, Number(r.data.offset) || 0);
        setCanManageAccountSecurity(r.data.can_manage_account_security === true);
        if ((nextTotal === 0 && nextOffset > 0) || (nextTotal > 0 && nextOffset >= nextTotal)) {
          setUsersOffset(nextTotal === 0 ? 0 : Math.floor((nextTotal - 1) / nextLimit) * nextLimit);
          return;
        }
        setUsers(nextUsers);
        setUsersTotal(nextTotal);
        setUsersLimit(nextLimit);
        setUsersOffset(nextOffset);
      })
      .catch(() => {
        if (requestId !== usersRequest.current) return;
        setUsers([]);
        setUsersTotal(0);
        setCanManageAccountSecurity(false);
        setUsersError(true);
        toast.error('Kontosuche konnte nicht geladen werden');
      })
      .finally(() => {
        if (requestId === usersRequest.current) setUsersLoading(false);
      });
  }, [fromFilter, pageSize, roleFilter, search, statusFilter, toFilter, usersOffset]);
  loadUsersRef.current = loadUsers;

  useEffect(() => {
    if (mode === 'directory') loadUsers();
  }, [mode, loadUsers]);

  const resetDirectoryOffset = (setter, value) => {
    setUsersOffset(0);
    setter(value);
  };

  const applyUserSearch = () => {
    const next = searchDraft.trim();
    if (next === search && usersOffset === 0) loadUsers();
    else {
      setUsersOffset(0);
      setSearch(next);
    }
  };

  const resetDirectoryFilters = () => {
    setSearchDraft('');
    setSearch('');
    setRoleFilter('customer');
    setStatusFilter('');
    setFromFilter('');
    setToFilter('');
    setUsersOffset(0);
  };

  const applyContextSearch = () => {
    const next = contextDraft.trim();
    if (next === contextSearch) loadRecent();
    else setContextSearch(next);
  };

  const clearContextSearch = () => {
    setContextDraft('');
    if (contextSearch) setContextSearch('');
    else loadRecent();
  };

  const toggleBan = async (user) => {
    const banning = user.status !== 'banned';
    // KONTO-TRENNUNG-001: Die Sperre trifft NUR dieses Konto — der Dialog sagt, welches.
    if (!window.confirm(`${accountKindLabel(user.role)} ${banning ? 'sperren?' : 'entsperren?'} — ${user.name}`)) return;
    try {
      await api.patch(`/admin/users/${user.id}/ban`, { ban: banning });
      toast.success(banning ? `${accountKindLabel(user.role)} von ${user.name} gesperrt` : `${accountKindLabel(user.role)} von ${user.name} entsperrt`);
      loadUsersRef.current?.();
    } catch {
      toast.error('Fehler');
    }
  };

  const recentCustomers = [];
  const seenCustomerIds = new Set();
  for (const ride of recentRides) {
    if (!ride.customer_id || seenCustomerIds.has(ride.customer_id)) continue;
    seenCustomerIds.add(ride.customer_id);
    recentCustomers.push(ride);
    if (recentCustomers.length === 25) break;
  }

  const activeRideCustomers = recentRides.filter(ride =>
    ['searching', 'accepted', 'arrived', 'in_progress'].includes(ride.status)).length;

  const firstVisibleUser = usersTotal === 0 ? 0 : usersOffset + 1;
  const lastVisibleUser = usersTotal === 0 ? 0 : Math.min(usersOffset + users.length, usersTotal);
  const hasPreviousUsers = usersOffset > 0;
  const hasNextUsers = usersOffset + usersLimit < usersTotal;

  const refresh = () => {
    if (mode === 'recent') loadRecent();
    else loadUsers();
  };

  return (
    <div className="users-page">
      {detailId && <UserDetailModal userId={detailId} onClose={() => setDetailId(null)} />}
      {securityUser && (
        <SecurityDialog
          key={securityUser.id}
          user={securityUser}
          returnFocus={securityTrigger.current}
          onClose={() => setSecurityUser(null)}
        />
      )}

      <header className="users-heading">
        <div>
          <span className="users-eyebrow"><UsersRound size={13} /> VOOGO Customer Operations</span>
          <h1>Kunden</h1>
          <p>Kunden ohne exakte Kontaktdaten über aktuelle Aktivität oder bekannte Zusammenhänge finden.</p>
        </div>
        <div className="users-heading-actions">
          <span className="users-total-badge">
            <UsersRound size={13} />
            {totalCustomers == null ? '—' : Number(totalCustomers).toLocaleString('de-DE')} Gesamtkunden
          </span>
          <span><ShieldCheck size={13} /> begrenzter Abruf</span>
          <span><UserRound size={13} /> max. {pageSize} sichtbar</span>
          <button onClick={refresh} disabled={mode === 'recent' ? recentLoading : usersLoading}>
            <RefreshCw size={14} />
            {(mode === 'recent' ? recentLoading : usersLoading) ? 'Lädt …' : 'Aktualisieren'}
          </button>
        </div>
      </header>

      <section className="users-mode-switch" aria-label="Kundenansicht">
        <button className={mode === 'recent' ? 'is-active' : ''} onClick={() => setMode('recent')}>
          <Sparkles size={14} />
          <span><strong>Kürzlich relevant</strong><small>Ohne Namen über Aktivität finden</small></span>
        </button>
        <button className={mode === 'directory' ? 'is-active' : ''} onClick={() => setMode('directory')}>
          <Search size={14} />
          <span><strong>Kontenübersicht</strong><small>Blättern, filtern oder optional suchen</small></span>
        </button>
      </section>

      {mode === 'recent' ? (
        <>
          <section className="users-discovery-panel">
            <header>
              <div>
                <span><History size={15} /></span>
                <div>
                  <small>Discovery-Modus</small>
                  <strong>Aus höchstens 50 aktuellen Fahrten</strong>
                </div>
              </div>
              <p>Mehrfach vorkommende Kunden werden zusammengeführt; sichtbar bleiben maximal 25 eindeutige Konten.</p>
            </header>

            <div className="users-discovery-grid">
              <label className="users-context-field">
                <span>Bekannter Zusammenhang</span>
                <div>
                  <Search size={14} />
                  <input
                    value={contextDraft}
                    onChange={event => setContextDraft(event.target.value)}
                    onKeyDown={event => event.key === 'Enter' && applyContextSearch()}
                    placeholder="Ort, Ziel, Partner oder Fahrer …"
                  />
                </div>
              </label>
              <div className="users-context-actions">
                <button className="is-search" onClick={applyContextSearch}><Search size={13} /> Eingrenzen</button>
                <button className="is-clear" onClick={clearContextSearch}>Zurücksetzen</button>
              </div>
              <div className="users-discovery-stat">
                <small>Eindeutige Kunden</small>
                <strong>{recentCustomers.length}</strong>
                <span>aus {recentRides.length} Fahrten</span>
              </div>
              <div className="users-discovery-stat">
                <small>Aktive Vorgänge</small>
                <strong>{activeRideCustomers}</strong>
                <span>im geladenen Ausschnitt</span>
              </div>
            </div>
          </section>

          <section className="users-status-strip" aria-label="Letzter Fahrtstatus">
            {recentStatusFilters.map(filter => (
              <button
                key={filter.value}
                className={recentStatus === filter.value ? 'is-active' : ''}
                onClick={() => setRecentStatus(filter.value)}
              >
                <i style={{ '--user-filter-color': statusColors[filter.value] || '#1b1c1f' }} />
                {filter.label}
              </button>
            ))}
          </section>

          <section className="users-recent-card">
            <header>
              <div>
                <span className="users-overline">Kunden-Discovery</span>
                <h2>Kürzlich relevante Kunden</h2>
              </div>
              <span><Clock3 size={11} /> neueste Aktivität zuerst</span>
            </header>

            {recentLoading ? (
              <div className="users-loading">
                {[0, 1, 2, 3, 4, 5].map(index => <Skeleton key={index} h={45} />)}
              </div>
            ) : recentCustomers.length === 0 ? (
              <Empty
                icon={UsersRound}
                title="Keine Kunden im gewählten Ausschnitt"
                hint="Passe den Zusammenhang oder den letzten Fahrtstatus an."
              />
            ) : (
              <div className="users-table-scroll">
                <table className="users-recent-table">
                  <thead>
                    <tr>
                      <th>Kunde</th>
                      <th>Letzte Aktivität</th>
                      <th>Letzte Strecke</th>
                      <th>Zuordnung</th>
                      <th>Fahrtstatus</th>
                      <th>Profil</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCustomers.map(ride => (
                      <tr key={ride.customer_id}>
                        <td>
                          <div className="users-customer-cell">
                            <span>{initials(ride.customer_name)}</span>
                            <div>
                              <strong>{ride.customer_name || 'Unbenanntes Konto'}</strong>
                              <small>{String(ride.customer_id).slice(0, 13)}…</small>
                            </div>
                          </div>
                        </td>
                        <td className="users-activity-time">{fmtDate(ride.created_at, true)}</td>
                        <td>
                          <div className="users-route-cell">
                            <span><i /> {ride.pickup_address || '—'}</span>
                            <span><i /> {ride.dropoff_address || '—'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="users-assignment-cell">
                            <strong>{ride.driver_name || 'Kein Fahrer'}</strong>
                            <small>{ride.company_name || 'Kein Partner'}</small>
                          </div>
                        </td>
                        <td>
                          <span
                            className="users-ride-status"
                            style={{ '--user-ride-status': statusColors[ride.status] || '#6b7280' }}
                          >
                            <i /> {statusLabels[ride.status] || ride.status}
                          </span>
                        </td>
                        <td>
                          <button className="users-profile-button" onClick={() => setDetailId(ride.customer_id)}>
                            Profil <ArrowRight size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : (
        <>
          <section className="users-account-panel">
            <header>
              <div>
                <span><Search size={15} /></span>
                <div>
                  <small>Konten durchsuchen</small>
                  <strong>Ohne Vorwissen blättern oder gezielt filtern</strong>
                </div>
              </div>
              <p>Der Server liefert immer nur eine Seite mit höchstens {pageSize} sparsamen Kontoeinträgen.</p>
            </header>

            <div className="users-account-grid">
              <div className="users-role-filter" aria-label="Kontorolle">
                {[
                  { value: 'customer', label: 'Kunden' },
                  { value: 'driver', label: 'Fahrer' },
                  { value: '', label: 'Alle Konten' },
                ].map(filter => (
                  <button
                    key={filter.value}
                    className={roleFilter === filter.value ? 'is-active' : ''}
                    onClick={() => resetDirectoryOffset(setRoleFilter, filter.value)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              <label className="users-account-search">
                <span>Optionale Suche</span>
                <div>
                  <Search size={14} />
                  <input
                    value={searchDraft}
                    onChange={event => setSearchDraft(event.target.value)}
                    onKeyDown={event => event.key === 'Enter' && applyUserSearch()}
                    placeholder="Name, E-Mail oder Telefonnummer …"
                  />
                </div>
              </label>
              <label className="users-directory-field">
                <span>Kontostatus</span>
                <select
                  aria-label="Kontostatus"
                  value={statusFilter}
                  onChange={event => resetDirectoryOffset(setStatusFilter, event.target.value)}
                >
                  <option value="">Alle operativen</option>
                  <option value="active">Aktiv</option>
                  <option value="banned">Gesperrt</option>
                  <option value="deleted">Gelöscht</option>
                </select>
              </label>
              <label className="users-directory-field">
                <span>Registriert ab</span>
                <input
                  aria-label="Registriert ab"
                  type="date"
                  value={fromFilter}
                  onChange={event => resetDirectoryOffset(setFromFilter, event.target.value)}
                />
              </label>
              <label className="users-directory-field">
                <span>Registriert bis</span>
                <input
                  aria-label="Registriert bis"
                  type="date"
                  value={toFilter}
                  onChange={event => resetDirectoryOffset(setToFilter, event.target.value)}
                />
              </label>
              <div className="users-directory-filter-actions">
                <button className="users-account-submit" onClick={applyUserSearch}><Search size={13} /> Suchen</button>
                <button className="users-account-reset" onClick={resetDirectoryFilters}>Zurücksetzen</button>
              </div>
            </div>
          </section>

          <section className="users-directory-card">
            <header>
              <div>
                <span className="users-overline">Kontenverzeichnis</span>
                <h2>Kunden, Fahrer und Konten</h2>
              </div>
              <span><UserRound size={11} /> {firstVisibleUser}–{lastVisibleUser} von {usersTotal.toLocaleString('de-DE')} Konten</span>
            </header>

            {usersLoading ? (
              <div className="users-loading"><Skeleton h={130} r={10} /></div>
            ) : usersError ? (
              <div className="users-directory-error" role="alert">
                <span>Kontenverzeichnis konnte nicht geladen werden.</span>
                <button onClick={loadUsers}><RefreshCw size={12} /> Erneut versuchen</button>
              </div>
            ) : users.length === 0 ? (
              <Empty
                icon={Search}
                title="Keine Konten im gewählten Ausschnitt"
                hint="Passe Rolle, Status, Zeitraum oder den optionalen Suchbegriff an."
              />
            ) : (
              <div>
                <div className="users-table-scroll">
                  <table className="users-directory-table">
                    <thead>
                      <tr>
                        {['Name', 'Rolle', 'Fahrten', 'Registriert', 'Status', 'Aktionen']
                          .map(label => <th key={label}>{label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.id}>
                          <td>
                            <div className="users-customer-cell">
                              <span>{initials(user.name)}</span>
                              <strong>{user.name}</strong>
                            </div>
                          </td>
                          <td>
                            <div className="users-role-stack">
                              <span className={`users-role is-${user.role}`}>
                                {{ customer: 'Kunde', driver: 'Fahrer', partner: 'Partner', admin: 'Admin' }[user.role] || user.role}
                              </span>
                              {user.shared_identity?.roles?.length > 1 && (
                                <small title="Getrennte Konten derselben Person — keine Datenübernahme, kein Sprung">Gleiche E-Mail: {user.shared_identity.roles
                                  .map(value => roleLabels[value] || value)
                                  .join(' · ')} (getrennte Konten)</small>
                              )}
                            </div>
                          </td>
                          <td className="users-number-cell">{user.total_rides || 0}</td>
                          <td className="users-date-cell">{fmtDate(user.created_at)}</td>
                          <td>
                            <span className={`users-account-status is-${user.status || 'active'}`}>
                              <i /> {accountStatusLabels[user.status] || user.status || 'Aktiv'}
                            </span>
                          </td>
                          <td>
                            <div className="users-row-actions">
                              {user.role === 'customer' && user.status !== 'deleted' && (
                                <button className="is-profile" onClick={() => setDetailId(user.id)}>
                                  <Car size={11} /> Fahrten
                                </button>
                              )}
                              {canManageAccountSecurity && ['customer', 'driver', 'partner'].includes(user.role) && user.status === 'active' && (
                                <button
                                  className="is-security"
                                  onClick={event => {
                                    securityTrigger.current = event.currentTarget;
                                    setSecurityUser(user);
                                  }}
                                >
                                  <ShieldCheck size={11} /> 2FA verwalten
                                </button>
                              )}
                              {user.role !== 'admin' && user.status !== 'deleted' && (
                                <button
                                  className={user.status === 'banned' ? 'is-unban' : 'is-ban'}
                                  title={`${accountKindLabel(user.role)} ${user.status === 'banned' ? 'entsperren' : 'sperren'}`}
                                  aria-label={`${accountKindLabel(user.role)} von ${user.name} ${user.status === 'banned' ? 'entsperren' : 'sperren'}`}
                                  onClick={() => toggleBan(user)}
                                >
                                  {user.status === 'banned'
                                    ? <><CheckCircle size={11} /> Entsperren</>
                                    : <><Ban size={11} /> Sperren</>}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <footer className="users-directory-pagination">
                  <span>Seite {usersTotal === 0 ? 0 : Math.floor(usersOffset / usersLimit) + 1}</span>
                  <div>
                    {/* Seitengröße 25/50/100 — Wechsel springt auf Seite 1 (Owner-Entscheid 26.08.). */}
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', marginRight: 8 }}>
                      <select
                        aria-label="Einträge pro Seite"
                        value={pageSize}
                        onChange={(event) => { setPageSize(Number(event.target.value)); setUsersOffset(0); }}
                        style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, fontWeight: 600, color: '#374151' }}
                      >
                        {[25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
                      </select>
                      pro Seite
                    </label>
                    <button
                      disabled={!hasPreviousUsers || usersLoading}
                      onClick={() => setUsersOffset(Math.max(0, usersOffset - usersLimit))}
                    >
                      Zurück
                    </button>
                    <button
                      disabled={!hasNextUsers || usersLoading}
                      onClick={() => setUsersOffset(usersOffset + usersLimit)}
                    >
                      Weiter
                    </button>
                  </div>
                </footer>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
